import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { useHasPermission } from '@/hooks/use-has-permission';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useCurrentUserId } from '@/modules/service-desk/shared/employee';
import { mergeWhere } from '@/lib/table-query';
import {
  ApplicationsTable,
  useApplicationsTable,
} from '@/modules/service-desk/shared/components/applications-table';
import {
  useCancelMetlife,
  useMetlifeApplications,
  useWithdrawMetlife,
} from '../../hooks/use-metlife';
import { MetlifeApplication } from '../../types/metlife.types';
import { createMyMetlifeColumns } from '../metlife-columns/metlife-columns';
import { MetlifeDetailsDialog } from '../metlife-details-dialog/metlife-details-dialog';

export const MetlifeMyApplications = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userId = useCurrentUserId();
  const canApply = useHasPermission(HRM_PERMISSIONS.METLIFE_APPLICATION.APPLY);
  const canWithdraw = useHasPermission(HRM_PERMISSIONS.METLIFE_APPLICATION.WITHDRAW);
  const canCancel = useHasPermission(HRM_PERMISSIONS.METLIFE_APPLICATION.CANCEL);
  const table = useApplicationsTable();
  const [detailsTarget, setDetailsTarget] = useState<MetlifeApplication | null>(null);
  const [withdrawTarget, setWithdrawTarget] = useState<MetlifeApplication | null>(null);
  const [cancelTarget, setCancelTarget] = useState<MetlifeApplication | null>(null);

  const { data, isLoading, error } = useMetlifeApplications(
    {
      pageNo: table.pagination.pageIndex + 1,
      pageSize: table.pagination.pageSize,
      filter: mergeWhere({ UserId: userId }, table.columnWhere),
      order: table.order,
    },
    Boolean(userId)
  );
  const { mutateAsync: withdraw } = useWithdrawMetlife();
  const { mutateAsync: cancelPolicy } = useCancelMetlife();

  const columns = useMemo(
    () =>
      createMyMetlifeColumns({
        t,
        canApply,
        canWithdraw,
        canCancel,
        onViewDetails: setDetailsTarget,
        onEdit: (application) =>
          navigate(`/hr/service-desk/insurance/apply?id=${application.ItemId}`),
        onWithdraw: setWithdrawTarget,
        onCancelPolicy: setCancelTarget,
      }),
    [t, canApply, canWithdraw, canCancel, navigate]
  );

  return (
    <>
      <ApplicationsTable
        columns={columns}
        result={data}
        isLoading={isLoading}
        error={error}
        table={table}
      />
      <MetlifeDetailsDialog
        application={detailsTarget}
        onOpenChange={() => setDetailsTarget(null)}
      />
      <ConfirmationModal
        open={Boolean(withdrawTarget)}
        onOpenChange={(open) => !open && setWithdrawTarget(null)}
        title={t('WITHDRAW_METLIFE')}
        confirmText={t('WITHDRAW')}
        description={t('ARE_YOU_SURE_TO_WITHDRAW_METLIFE')}
        onConfirm={() => withdrawTarget && withdraw(withdrawTarget.ItemId)}
      />
      <ConfirmationModal
        open={Boolean(cancelTarget)}
        onOpenChange={(open) => !open && setCancelTarget(null)}
        title={t('CANCEL_METLIFE')}
        description={t('ARE_YOU_SURE_TO_CANCEL_METLIFE')}
        confirmText={t('CONFIRM')}
        onConfirm={() => cancelTarget && cancelPolicy({ itemId: cancelTarget.ItemId })}
      />
    </>
  );
};
